import { useEffect, useMemo } from "react";

import { formatCardBrand } from "@calcom/lib/format";
import type { Brands } from "@calcom/lib/getCardBrand";
import { getCardBrand } from "@calcom/lib/getCardBrand";
import type { RouterOutputs } from "@calcom/trpc/react";
import { trpc } from "@calcom/trpc/react";
import { Select } from "@calcom/ui";

export type StoredPaymentCard = RouterOutputs["viewer"]["patients"]["getCardList"][number];

export type PatientStoredPaymentMethodSelectProps = {
  officeId?: number;
  patientId?: number | undefined;
  /** When provided, skips the authenticated card list query (e.g. public sign pages). */
  cards?: StoredPaymentCard[];
  /** Selected stored method id (controlled). Omit when nothing selected yet. */
  value?: number;
  onCardChange: (card: StoredPaymentCard | undefined) => void;
  disabled?: boolean;
  /** When true, labels match virtual/insurance copy (default marker, "Bank :"). */
  showDefaultInLabel?: boolean;
  className?: string;
  selectClassName?: string;
  /**
   * When the list loads and `value` is still unset, select this id if present
   * (e.g. dialog opened with an existing instrument).
   */
  preferredCardId?: number | null;
  isLoading?: boolean;
};

export function PatientStoredPaymentMethodSelect({
  officeId,
  patientId,
  cards,
  value,
  onCardChange,
  disabled = false,
  showDefaultInLabel = false,
  className = "relative w-full",
  selectClassName = "rounded-xl",
  preferredCardId,
  isLoading: externalLoading = false,
}: PatientStoredPaymentMethodSelectProps) {
  const useProvidedCards = cards !== undefined;
  const { data: queriedCardList, isLoading: queryLoading } = trpc.viewer.patients.getCardList.useQuery(
    {
      patientId: Number(patientId),
      officeId: Number(officeId),
    },
    {
      enabled: !useProvidedCards && !!patientId && !!officeId,
      refetchInterval: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    }
  );

  const cardList = useProvidedCards ? cards : queriedCardList;
  const isLoading = useProvidedCards ? externalLoading : queryLoading;

  useEffect(() => {
    if (preferredCardId == null) return;
    if (value !== undefined) return;
    const match = cardList?.find((c) => c.id === preferredCardId);
    if (match) {
      onCardChange(match);
    }
    // Intentionally omit onCardChange: parents often pass inline handlers; selection is keyed by value/preferredCardId.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardList, preferredCardId, value]);

  const cardOptions = useMemo(
    () =>
      cardList?.map((c) => ({
        label: `${
          c.type === "PAYMENT_CARD"
            ? formatCardBrand(c.brand as Brands) +
              " Last 4: " +
              c.lastFour +
              ", Exp " +
              c.expMonth +
              "/" +
              c.expYear +
              (showDefaultInLabel && c.selected ? " (Default)" : "")
            : (showDefaultInLabel ? "Bank : " : "Bank: ") +
              c.maskedAccountNumber +
              (showDefaultInLabel && c.selected ? " (Default)" : "")
        }`,
        value: c.id,
      })),
    [cardList, showDefaultInLabel]
  );

  const selectedCard = useMemo(() => cardList?.find((c) => c.id === value), [cardList, value]);

  return (
    <div className={className}>
      <Select
        className={selectClassName}
        options={cardOptions}
        value={cardOptions?.find((o) => o.value === value)}
        onChange={(option) => {
          if (option) {
            const next = cardList?.find((c) => c.id === option.value);
            if (next) onCardChange(next);
          } else {
            onCardChange(undefined);
          }
        }}
        hasIcon={!!selectedCard}
        isDisabled={disabled}
        isLoading={isLoading}
      />
      {selectedCard && (
        <img
          src={
            getCardBrand(
              `${
                selectedCard.brand ? selectedCard.brand + ":" + selectedCard.lastFour : selectedCard.lastFour
              }`
            ).logo
          }
          alt="card brand image"
          className="absolute top-2 left-1 h-5 w-5"
        />
      )}
    </div>
  );
}
